/**
 * Offline staff PIN verification.
 *
 * Specification: ../../test/staff-auth.spec.ts. Master plan §3 K2, §0.
 *
 * archive/v0's `/api/device/bootstrap` shipped the ENTIRE staff PIN hash table
 * to every paired device at PBKDF2 with 10 000 iterations. One stolen tablet
 * yielded every PIN in the company, and four-digit PINs at that cost fall in
 * seconds.
 *
 * The framing that makes a safe replacement possible: a PIN is a CONVENIENCE
 * CREDENTIAL OVER AN ALREADY-AUTHENTICATED DEVICE, never a primary one. The
 * device has already proved itself to the hub with an Ed25519 key. The PIN
 * answers "which of the eight people on this shift is holding the handheld",
 * and the threat model is a colleague reaching for someone else's account far
 * more than a remote attacker.
 *
 * Four properties make it adequate for that job:
 *
 *   - SCOPE. Only staff rostered on this shift.
 *   - DEVICE BINDING. The salt is derived from a secret only the paired device
 *     holds and is NEVER shipped, so a stolen bundle cannot even generate
 *     candidate verifiers.
 *   - SHORT LIFE. It expires with the shift.
 *   - RATE LIMITING, per staff member.
 *
 * PBKDF2 through WebCrypto rather than Argon2id, for one reason that overrides
 * the cryptographic preference: this must run in the browser, since the POS is
 * a PWA and the whole point is verifying with no network. `Bun.password` does
 * not exist there, and it self-salts, which a device-bound salt forbids.
 */

/**
 * Six digits, enforced at issue.
 *
 * Four digits is 10 000 candidates; six is a million. An attacker needs the
 * device secret regardless, but the margin between "an afternoon" and "a
 * month" is worth two extra taps from a server.
 */
export const MIN_PIN_LENGTH = 6;

/** Tuned so one verification is ~50–100 ms on a mid-range Android tablet. */
const PBKDF2_ITERATIONS = 600_000;
const VERIFIER_BITS = 256;

export interface RosteredStaff {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleId: string;
  /** Present only server-side, while the bundle is being built. */
  readonly pin: string;
}

export interface ShiftScope {
  readonly shiftId: string;
  readonly deviceId: string;
  readonly venueId: string;
  readonly expiresAt: Date;
}

/**
 * One person's offline credential.
 *
 * Note what is absent: the PIN, and the salt. Without the salt — which the
 * device recomputes from a secret it alone holds — the verifier is not
 * attackable by anyone who merely obtained the bundle.
 */
export interface StaffCredential {
  readonly staffId: string;
  readonly displayName: string;
  readonly roleId: string;
  /** The roster epoch it was minted at. A device drops it when the roster moves on. */
  readonly epoch?: number;
  /** base64url. Meaningless without the device secret. */
  readonly verifier: string;
}

export interface ShiftCredentialBundle {
  readonly shiftId: string;
  readonly deviceId: string;
  readonly venueId: string;
  readonly expiresAt: string;
  readonly credentials: readonly StaffCredential[];
}

import { toBase64Url } from "../crypto";

const encoder = new TextEncoder();

/**
 * ONE base64url, from the kernel's crypto module.
 *
 * This had its own `Buffer`-based copy, which meant the POS could not compute a
 * PIN verifier in a browser at all — `Buffer` is a Node global that does not
 * exist there. A second implementation is a second thing to get wrong in a
 * second environment.
 */
const b64u = (bytes: ArrayBuffer): string => toBase64Url(bytes);

/**
 * The salt for one (device, shift, staff) triple.
 *
 * Derived rather than stored, and never transmitted. This is the single line
 * that makes a leaked bundle inert: an attacker cannot compute a candidate
 * verifier without the device secret, so brute force has nothing to compare
 * against.
 *
 * Binding the shift too means last week's bundle cannot be replayed onto
 * tonight's.
 */
async function saltFor(
  deviceSecret: string,
  shiftId: string,
  staffId: string,
  // Uint8Array<ArrayBuffer>, not the default Uint8Array<ArrayBufferLike>.
  // WebCrypto's BufferSource requires a non-shared backing store, and the POS
  // compiles with the DOM lib while the kernel's own tsconfig does not — so
  // the loose type passed here and failed there. Shared code has to satisfy
  // the strictest consumer.
): Promise<Uint8Array<ArrayBuffer>> {
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(deviceSecret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  // NUL as the domain separator, written explicitly rather than as a space.
  // A printable separator lets ("a b", "c") and ("a", "b c") derive the same
  // salt; NUL cannot occur in an identifier, so the concatenation is
  // unambiguous. Theoretical for UUID-shaped ids, free to get right.
  const mac = await crypto.subtle.sign(
    "HMAC",
    key,
    encoder.encode(`${shiftId}\u0000${staffId}`),
  );
  const salt = new Uint8Array(new ArrayBuffer(mac.byteLength));
  salt.set(new Uint8Array(mac));
  return salt;
}

async function deriveVerifier(
  pin: string,
  staffId: string,
  deviceSecret: string,
  shiftId: string,
): Promise<string> {
  const salt = await saltFor(deviceSecret, shiftId, staffId);
  const material = await crypto.subtle.importKey(
    "raw",
    // Defence in depth, and labelled as such. The SALT already separates one
    // staff member from another, so this binding is redundant today — it
    // matters only if somebody later "simplifies" the salt derivation. It is
    // therefore not independently testable, and claiming otherwise in a
    // comment would be a lie a future reader relies on.
    encoder.encode(`${pin}\u0000${staffId}`),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations: PBKDF2_ITERATIONS },
    material,
    VERIFIER_BITS,
  );
  return b64u(bits);
}

/**
 * Build the bundle a device caches for one shift. Server-side.
 *
 * Takes the roster, not the staff table. Passing the whole company would be
 * the archive/v0 bug restored, so the signature makes it awkward to do by
 * accident.
 */
export async function buildShiftCredentials(
  scope: ShiftScope,
  roster: readonly RosteredStaff[],
  deviceSecret: string,
): Promise<ShiftCredentialBundle> {
  for (const staff of roster) {
    if (staff.pin.length < MIN_PIN_LENGTH) {
      // Refused at issue rather than at use, so a weak PIN never reaches a
      // device at all.
      throw new Error(
        `staff-auth: ${staff.staffId} has a ${staff.pin.length}-digit PIN; ` +
          `${MIN_PIN_LENGTH} is the minimum`,
      );
    }
  }

  const credentials = await Promise.all(
    roster.map(async (staff) => ({
      staffId: staff.staffId,
      displayName: staff.displayName,
      roleId: staff.roleId,
      verifier: await deriveVerifier(staff.pin, staff.staffId, deviceSecret, scope.shiftId),
    })),
  );

  return {
    shiftId: scope.shiftId,
    deviceId: scope.deviceId,
    venueId: scope.venueId,
    expiresAt: scope.expiresAt.toISOString(),
    credentials,
  };
}

/**
 * Mint ONE device-bound credential, for one person, on one device.
 *
 * This is what replaced handing a device a bundle built from every PIN. The
 * server no longer holds a recoverable PIN (see ./storage.ts), so it can only
 * compute a verifier at the moment somebody presents theirs — which is their
 * first sign-in on that device. From then on the device verifies locally and
 * needs no network.
 *
 * The PIN is used and discarded here. It is never stored and never returned.
 */
export async function mintDeviceCredential(
  scope: ShiftScope,
  person: { readonly staffId: string; readonly displayName: string; readonly roleId: string; readonly epoch?: number },
  pin: string,
  deviceSecret: string,
): Promise<StaffCredential> {
  if (pin.length < MIN_PIN_LENGTH) {
    throw new Error(
      `staff-auth: ${person.staffId} presented a ${pin.length}-digit PIN; ` +
        `${MIN_PIN_LENGTH} is the minimum`,
    );
  }
  return {
    staffId: person.staffId,
    displayName: person.displayName,
    roleId: person.roleId,
    ...(person.epoch !== undefined ? { epoch: person.epoch } : {}),
    verifier: await deriveVerifier(pin, person.staffId, deviceSecret, scope.shiftId),
  };
}

export type PinRejection = "EXPIRED" | "NOT_ROSTERED" | "BAD_PIN";

export type PinResult =
  | { readonly ok: true; readonly staff: StaffCredential }
  | { readonly ok: false; readonly reason: PinRejection };

/** Length-independent, early-exit-free comparison. */
function constantTimeEquals(a: string, b: string): boolean {
  // Compare over a fixed span so the loop count does not reveal where the
  // first difference is. A device sitting on a bar is physically accessible,
  // and a timing oracle over six digits is a real reduction.
  const length = Math.max(a.length, b.length);
  let difference = a.length ^ b.length;
  for (let i = 0; i < length; i++) {
    difference |= (a.charCodeAt(i) || 0) ^ (b.charCodeAt(i) || 0);
  }
  return difference === 0;
}

/**
 * Verify a PIN against the cached bundle. Entirely local.
 *
 * The expiry check comes first and is deliberately cheap: an expired bundle
 * should not spend 80 ms of a tablet's CPU before saying so.
 */
export async function verifyStaffPin(
  bundle: ShiftCredentialBundle,
  staffId: string,
  pin: string,
  deviceSecret: string,
  now: Date,
): Promise<PinResult> {
  if (now.getTime() >= Date.parse(bundle.expiresAt)) return { ok: false, reason: "EXPIRED" };

  const credential = bundle.credentials.find((c) => c.staffId === staffId);
  if (!credential) return { ok: false, reason: "NOT_ROSTERED" };

  const candidate = await deriveVerifier(pin, staffId, deviceSecret, bundle.shiftId);
  return constantTimeEquals(candidate, credential.verifier)
    ? { ok: true, staff: credential }
    : { ok: false, reason: "BAD_PIN" };
}

// --- Rate limiting ---------------------------------------------------------

export interface LimiterOptions {
  readonly maxAttempts: number;
  readonly lockoutMs: number;
}

/**
 * Wrong-PIN throttling, PER STAFF MEMBER.
 *
 * Per device would be catastrophic in practice: one server mistyping four
 * times would take the till out of service during a rush, which is a far worse
 * outcome than the attack it prevents. Lockouts also expire on their own — a
 * manager unlock is one more thing to need at the worst moment.
 */
export class PinAttemptLimiter {
  readonly #options: LimiterOptions;
  readonly #failures = new Map<string, { count: number; lockedUntil: number }>();

  constructor(options: LimiterOptions) {
    this.#options = options;
  }

  isLocked(staffId: string, now: Date): boolean {
    const entry = this.#failures.get(staffId);
    if (!entry) return false;
    if (entry.lockedUntil <= now.getTime()) {
      this.#failures.delete(staffId);
      return false;
    }
    return entry.count >= this.#options.maxAttempts;
  }

  /**
   * When the lockout lifts, or null when there is none.
   *
   * Exposed so a screen can count down honestly. Without it the only way to
   * tell a server how long to wait is to repeat the CONFIGURED window — which
   * says "try again in 60 seconds" to somebody who has already waited forty,
   * and a countdown that does not count down is one people tap through.
   */
  lockedUntil(staffId: string, now: Date): number | null {
    if (!this.isLocked(staffId, now)) return null;
    return this.#failures.get(staffId)?.lockedUntil ?? null;
  }

  remaining(staffId: string, now: Date): number {
    if (this.isLocked(staffId, now)) return 0;
    const entry = this.#failures.get(staffId);
    return Math.max(0, this.#options.maxAttempts - (entry?.count ?? 0));
  }

  recordFailure(staffId: string, now: Date): void {
    const entry = this.#failures.get(staffId);
    const count = (entry && entry.lockedUntil > now.getTime() ? entry.count : 0) + 1;
    this.#failures.set(staffId, { count, lockedUntil: now.getTime() + this.#options.lockoutMs });
  }

  recordSuccess(staffId: string): void {
    this.#failures.delete(staffId);
  }
}
